import {
  Component,
  forwardRef,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  effect,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormGroup,
  AbstractControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { SharedSignalsService } from '../../services/shared-signals.service';

@Component({
  selector: 'taly-custom-input-group',
  templateUrl: './custom-input-group.component.html',
  styleUrls: ['./custom-input-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputGroupComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomInputGroupComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomInputGroupComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  transferSignal = inject(SharedSignalsService);
  private controlStatusSubscription: Subscription | null = null;
  private subscribedControl: AbstractControl | null = null;

  @Input({ required: true }) id: string = '';
  @Input({ required: true }) label: string = '';
  @Input() placeholderText?: string = '';
  @Input() inputType?: any = 'input';
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() min!: number;
  @Input() max!: number;
  @Input() inputmodeType: string = 'none';
  @Input() autofocus: boolean = false;

  @Input() set isDisabled(disable: boolean) {
    this.disabled = disable;
  }

  disabled = false;
  textDirection: string;
  get f() {
    return this.formGroup?.controls;
  }

  constructor() {
    effect(() => {
      this.textDirection = this.transferSignal.textDirection();
    });
  }

  ngOnChanges() {
    this.subscribeToControlStatusChanges();
  }

  ngOnDestroy() {
    this.controlStatusSubscription?.unsubscribe();
  }

  private _value: any | null = null;

  set value(value: any) {
    this._value = value;
    this.onChange();
  }

  get value(): any {
    return this._value;
  }

  onChange() {}

  onReset() {
    if (this.formGroup && this.controlName) {
      this.formGroup.get(this.controlName)?.setValue(null); // Set the value in the form control
    }
  }

  onTouched = () => {};

  writeValue(value: any) {
    this.value = value;
    if (this.changeDetectorRef) {
      this.changeDetectorRef.markForCheck();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // Implement the validator function
  validate() {
    return null;
  }

  private subscribeToControlStatusChanges(): void {
    const control = this.formGroup?.get(this.controlName);

    if (!control || control === this.subscribedControl) {
      return;
    }

    this.controlStatusSubscription?.unsubscribe();
    this.subscribedControl = control;
    this.controlStatusSubscription = control.statusChanges.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
    this.changeDetectorRef.markForCheck();
  }
}
